import React, { useState } from 'react';
import Layout from '../../components/layout/layout';
import Button from '../../components/ui/button';
import HubSpotModal from '../../components/ui/hubspot-modal';
import { WebPageSchema, BreadcrumbSchema } from '../../lib/utils/schema-utils';
import HeroTemplateEnts from '../../components/sections/HeroTemplateEnts';

const RecrutementPage = () => {
  const [isHubSpotModalOpen, setIsHubSpotModalOpen] = useState(false);

  return (
    <Layout>
  <h1 className="sr-only">Cabinet de recrutement à Marseille</h1>

  <WebPageSchema
    page={{
      title: "Cabinet de recrutement à Marseille | recruteurs.io",
      description: "Cabinet de recrutement innovant à Marseille. Expertise RH, IA et approche sur-mesure pour vos recrutements. Découvrez notre méthode unique.",
      url: "https://recruteurs.io/entreprise/recrutement",
      lastReviewed: "2025-05-22"
    }}
  />

  <BreadcrumbSchema
    items={[
      { name: "Accueil", url: "https://recruteurs.io" },
      { name: "Entreprise", url: "https://recruteurs.io/entreprise" },
      { name: "Recrutement", url: "https://recruteurs.io/entreprise/recrutement" }
    ]}
  />

  <HeroTemplateEnts
    page="recrutement"
    section="hero"
    title="Recrutez des talents, qui vous ressemblent !"
    description="Le cabinet qui révolutionne le recrutement en combinant expertise RH, technologies innovantes. Le tout, avec un modèle financier unique."
    imageUrl="https://ygkvhunsrhvgddngoulm.supabase.co/storage/v1/object/public/backgrounds/backgrounds/Recrutement-de-cadres-marseille.webp"
    breadcrumbs={[
      { name: "Entreprise", href: "/entreprise" },
      { name: "Recrutement", href: "/entreprise/recrutement" }
    ]}
    cta1={{
      label: "Tester gratuitement",
      onClick: () => setIsHubSpotModalOpen(true),
    }}
    cta2={{
      label: "Planifier un appel",
      href: "/infos/contact",
    }}
  />

      <HubSpotModal 
        isOpen={isHubSpotModalOpen}
        onClose={() => setIsHubSpotModalOpen(false)}
      />

      <>
        <section id="expertise" className="py-20 bg-white px-6 md:px-16 lg:grid lg:grid-cols-2 lg:gap-16 items-center">
          <div>
            <h2 className="text-3xl md:text-4xl font-bold mb-6">
              Recrutez les <span className="text-gradient">bons profils</span>, pas juste des CV
            </h2>
            <ul className="list-disc pl-5 space-y-4 text-neutral-700 text-lg">
              <li>
                <strong>Sourcing ciblé :</strong> vos recherches sont pilotées par un consultant expérimenté, pas par un junior.
              </li>
              <li>
                <strong>Expérience candidat engageante :</strong> chaque profil est immergé dans votre culture d'entreprise, dès le premier contact.
              </li>
              <li>
                <strong>Rencontres à forte valeur ajoutée :</strong> nous faisons le tri, vous gagnez du temps sur les meilleurs.
              </li>
            </ul>
            <div className="mt-8">
              <a href="#processus" className="bg-neutral-900 text-white px-6 py-3 rounded-full font-medium hover:scale-105 transition">
                Notre méthode de sélection
              </a>
            </div>
          </div>
          <div className="mt-10 lg:mt-0">
            <img
              src="https://ygkvhunsrhvgddngoulm.supabase.co/storage/v1/object/public/backgrounds/backgrounds/Onboarding%20marseille.png"
              alt="Recrutement RH ciblé marseille"
              className="w-full"
              style={{
                background: "transparent",
                backgroundColor: "transparent",
                borderRadius: '0.75rem',
                boxShadow: 'none'
              }}
            />
          </div>
        </section>

        <section id="processus" className="py-20 bg-gray-50 px-6 md:px-16 lg:grid lg:grid-cols-2 lg:gap-16 items-center">
          <div className="mb-10 lg:mb-0">
            <img
              src="https://ygkvhunsrhvgddngoulm.supabase.co/storage/v1/object/public/backgrounds/backgrounds/pilotage%20des%20recrutements.webp"
              alt="Pilotage de vos recrutements à marseille "
              className="w-full"
              style={{
                background: "transparent",
                backgroundColor: "transparent",
                borderRadius: '0.75rem',
                boxShadow: 'none'
              }}
            />
          </div>
          <div>
            <h2 className="text-3xl md:text-4xl font-bold mb-6">
              Reprenez le contrôle <span className="text-gradient">de votre temps RH</span>
            </h2>
            <p className="text-lg text-neutral-700 mb-6">
              Trop de temps perdu à gérer des candidatures inutiles ? On a tout repensé pour vous faire gagner en sérénité :
            </p>
            <ul className="list-disc pl-5 space-y-4 text-neutral-700 text-lg">
              <li><strong>Automatisation des relances</strong> et prises de RDV</li>
              <li><strong>Tableau de bord interactif</strong> pour suivre vos candidats en temps réel</li>
              <li><strong>Fiches profils claires</strong> et accessibles à tout moment</li>
            </ul>
            <div className="mt-8">
              <a href="#filtrage" className="bg-neutral-900 text-white px-6 py-3 rounded-full font-medium hover:scale-105 transition">
                Découvrez notre dashboard RH
              </a>
            </div>
          </div>
        </section>

        <section id="filtrage" className="py-20 bg-white px-6 md:px-16 lg:grid lg:grid-cols-2 lg:gap-16 items-center">
          <div>
            <h2 className="text-3xl md:text-4xl font-bold mb-6">
              Sécurisez vos <span className="text-gradient">embauches</span>
            </h2>
            <ul className="list-disc pl-5 space-y-4 text-neutral-700 text-lg">
              <li><strong>Filtres à chaque étape</strong> du process</li>
              <li><strong>Sélection rigoureuse :</strong> seuls les plus motivés accèdent à la finale</li>
              <li><strong>Transparence totale</strong> sur les profils transmis</li>
              <li><strong>Tests personnalisés</strong> + entretiens structurés</li>
              <li><strong>Preboarding & onboarding</strong> inclus pour fluidifier l'arrivée</li>
            </ul>
            <div className="mt-8">
              <a href="#tarifs" className="bg-neutral-900 text-white px-6 py-3 rounded-full font-medium hover:scale-105 transition">
                Bénéficiez de notre méthodologie
              </a>
            </div>
          </div>
          <div className="mt-10 lg:mt-0">
            <img
              src="https://ygkvhunsrhvgddngoulm.supabase.co/storage/v1/object/public/backgrounds/backgrounds/testrh.webp"
              alt="Onboarding outil RH marseille"
              className="w-full"
              style={{
                background: "transparent",
                backgroundColor: "transparent",
                borderRadius: '0.75rem',
                boxShadow: 'none'
              }}
            />
          </div>
        </section>

        <section id="tarifs" className="py-20 bg-gray-50 text-center px-6 md:px-12">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              Un modèle <span className="text-gradient">financier repensé</span>
            </h2>
            <p className="text-lg text-neutral-700 mb-6">
              Pas de commission floue ou de frais fixes injustifiés : vous ne payez que pour les étapes franchies. Un modèle qui valorise la performance, pas juste la présentation de candidats.
            </p>
            <a href="/tarifs" className="inline-block bg-gradient-to-r from-pink-500 to-orange-400 text-white px-6 py-3 rounded-full font-medium hover:scale-105 transition">
              Découvrez nos tarifs
            </a>
          </div>
        </section>

        {isHubSpotModalOpen && (
          <HubSpotModal
            isOpen={isHubSpotModalOpen}
            onClose={() => setIsHubSpotModalOpen(false)}
          />
        )}
      </>
<section className="bg-white py-16 px-6 md:px-16">
  <div className="max-w-4xl mx-auto text-lg text-neutral-800 leading-relaxed">
    <h2 className="text-3xl font-bold mb-6 text-transparent bg-clip-text bg-gradient-to-r from-[#d03484] to-[#d47254]">
      Cabinet de recrutement à Marseille : notre méthode unique
    </h2>
    <p className="mb-4">
      Depuis 2007, notre cabinet de recrutement à Marseille accompagne les entreprises dans la transformation de leur stratégie RH. Fort de notre expérience en direction des ressources humaines, notamment en contexte dégradé, nous avons bâti une méthode innovante mêlant expertise humaine et intelligence artificielle.
    </p>
    <p className="mb-4">
      Nous intervenons sur l'ensemble du processus de recrutement : <strong>sourcing ciblé</strong>, <strong>filtrage intelligent</strong>, <strong>onboarding structuré</strong> et <strong>audit RH</strong> complet. Grâce à nos outils propriétaires et notre approche IA augmentée, nous vous faisons gagner du temps tout en améliorant la qualité des recrutements.
    </p>
    <p className="mb-4">
      Notre modèle repose sur une <a href="https://recruteurs.io/infos/tarifs" className="underline">tarification à la performance</a>, sans frais fixes cachés. Il s'adapte aussi bien aux campagnes de recrutement ponctuelles qu'à des démarches d'<a href="https://recruteurs.io/entreprise/externalisation" className="underline">externalisation RH</a>. Notre outil <a href="https://pimpmyjob.fr" className="underline">PimpMyJob</a> permet de générer des offres d’emploi sur-mesure en quelques clics.
    </p>
    <p>
      Vous voulez comprendre pourquoi nos clients nous font confiance ? Découvrez <a href="https://recruteurs.io/infos/charte" className="underline">notre manifeste RH</a> et rejoignez ceux qui transforment leur façon de recruter. Nous pensons que chaque recrutement est une rencontre stratégique, et notre mission est de vous aider à les réussir.
    </p>
  </div>
</section>
    </Layout>
  );
};

export default RecrutementPage;